import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import { Checkbox } from "../components/ui/checkbox";

import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";

import { auth, db } from "@/firebase";
import Loading from "@/components/Loader";

import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { coursesList } from "@/data/global_config";
import { useAuth } from "@/context/AuthContext";

import {
  collection,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";


const Login = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [authChecking, setAuthChecking] = useState(false);
  const [corporateLogin, setCorporateLogin] = useState(false);

  // AuthContext is the source of truth for Firebase authentication.
  // We only use loading here to wait for the initial Firebase auth check.
  const { loading: authLoading } = useAuth();


  /*
   * ------------------------------------------------------------
   * FORM SCHEMA
   * ------------------------------------------------------------
   */

  const formSchema = z.object({
    orgCode: z.string().min(1, {
      message: "Organization Code is required",
    }),

    code: z.string().min(1, {
      message: "Course Code is required",
    }),

    email: z
      .string()
      .min(1, {
        message: "Email is required",
      })
      .email({
        message: "Invalid email address",
      }),

    password: z.string().min(6, {
      message: "Password must be at least 6 characters.",
    }),
  });


  const form = useForm({
    resolver: zodResolver(formSchema),

    defaultValues: {
      orgCode: "",
      code: "",
      email: "",
      password: "",
    },
  });


  /*
   * ------------------------------------------------------------
   * NORMALIZE COURSE CODE
   * ------------------------------------------------------------
   *
   * Example:
   *
   * " 202603 " -> "202603"
   * "20 26 03" -> "202603"
   *
   * This makes the course code consistent between:
   *
   * - Form
   * - coursesList
   * - Firestore
   * - localStorage
   */

  const normalizeCode = (rawCode) => {
    return (rawCode || "")
      .replace(/\s+/g, "")
      .toLowerCase();
  };


  /*
   * ------------------------------------------------------------
   * VALIDATE COURSE
   * ------------------------------------------------------------
   */

  const validateCourse = () => {
    const rawCode = form.getValues("code");
    const code = normalizeCode(rawCode);

    if (!code) {
      toast({
        variant: "destructive",
        title: "Course Code Required!",
        description: "Please enter your course code",
      });

      return false;
    }

    if (!coursesList[code]) {
      toast({
        variant: "destructive",
        title: "Invalid Course Code!",
        description: "Please enter a valid course code",
      });

      return false;
    }

    return true;
  };


  /*
   * ------------------------------------------------------------
   * AUTHORIZE USER
   * ------------------------------------------------------------
   *
   * Firestore structure:
   *
   * CorporateClients
   *    └── {orgCode}
   *          ├── EnrolledCourses
   *          └── studentInfo
   *                 └── {studentId}
   *
   * Student email can exist as:
   *
   * attendance.email
   *
   * or
   *
   * email
   *
   * Same for isAdmin.
   */

  const authorizeUser = async (email, code, orgCode) => {
    try {
      if (!email || !code || !orgCode) {
        console.error("Missing authorization information:", {
          email,
          code,
          orgCode,
        });

        toast({
          variant: "destructive",
          title: "Authorization Failed!",
          description: "Organization and course information is required.",
        });

        return false;
      }


      /*
       * --------------------------------------------------------
       * CHECK ORGANIZATION
       * --------------------------------------------------------
       */

      const orgDocRef = doc(
        db,
        `CorporateClients/${orgCode}`
      );

      const orgDocSnap = await getDoc(orgDocRef);

      if (!orgDocSnap.exists()) {
        toast({
          variant: "destructive",
          title: "Invalid Organization Code!",
          description: "Please enter a valid org code",
        });

        return false;
      }


      /*
       * --------------------------------------------------------
       * CHECK COURSE
       * --------------------------------------------------------
       */

      const orgData = orgDocSnap.data();

      const enrolledCourses = orgData?.EnrolledCourses || [];

      const isCourseAvailable = enrolledCourses.includes(code);

      if (!isCourseAvailable) {
        toast({
          variant: "destructive",
          title: "Course Not Available!",
          description:
            "This course is not offered by the organization",
        });

        return false;
      }


      /*
       * --------------------------------------------------------
       * CHECK STUDENT
       * --------------------------------------------------------
       */

      const studentInfoRef = collection(
        db,
        `CorporateClients/${orgCode}/studentInfo`
      );

      const querySnapshot = await getDocs(studentInfoRef);

      let authorized = false;

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();

        /*
         * Support both possible Firestore structures:
         *
         * 1. attendance.email
         * 2. email
         */

        const studentEmail =
          data?.attendance?.email ??
          data?.email;

        const studentIsAdmin =
          data?.attendance?.isAdmin ??
          data?.isAdmin;


        if (
          studentEmail &&
          studentEmail.toLowerCase() === email.toLowerCase()
        ) {
          authorized = true;

          const studentId = docSnap.id;

          localStorage.setItem(
            "studentId",
            studentId
          );

          localStorage.setItem(
            "isAdmin",
            String(studentIsAdmin === true)
          );

          console.log("Student authorized:", {
            email,
            studentId,
            isAdmin: studentIsAdmin === true,
          });
        }
      });


      /*
       * --------------------------------------------------------
       * NOT AUTHORIZED
       * --------------------------------------------------------
       */

      if (!authorized) {
        toast({
          variant: "destructive",
          title: "Unauthorized Email!",
          description:
            "This email is not registered under the organization",
        });
      }

      return authorized;

    } catch (error) {
      console.error(
        "Authorization error:",
        error
      );

      toast({
        variant: "destructive",
        title: "Authorization Failed!",
        description:
          "Something went wrong. Please try again.",
      });

      return false;
    }
  };


  /*
   * ------------------------------------------------------------
   * EMAIL / PASSWORD SIGN-IN
   * ------------------------------------------------------------
   */

  const handleSignIn = async (values) => {
    if (authChecking) {
      return;
    }

    const {
      email,
      password,
      orgCode,
    } = values;

    const code = normalizeCode(values.code);


    /*
     * Validate course first.
     */

    const validCourse = validateCourse();

    if (!validCourse) {
      return;
    }


    /*
     * Email/password login is only available
     * when Corporate Login is enabled.
     */

    if (!corporateLogin) {
      return;
    }


    try {
      setAuthChecking(true);


      /*
       * IMPORTANT:
       *
       * Store these BEFORE Firebase sign-in.
       *
       * Firebase can trigger onAuthStateChanged
       * before signInWithEmailAndPassword() resolves.
       */

      localStorage.setItem(
        "courseCode",
        code
      );

      localStorage.setItem(
        "orgCode",
        orgCode
      );


      /*
       * Firebase authentication.
       */

      const credential =
        await signInWithEmailAndPassword(
          auth,
          email,
          password
        );


      const signedInUser = credential.user;


      console.log(
        "Email login successful:",
        signedInUser.email
      );


      /*
       * Check whether this authenticated user
       * is authorized for the selected organization
       * and course.
       */

      const isAuthorized =
        await authorizeUser(
          signedInUser.email,
          code,
          orgCode
        );


      /*
       * Unauthorized:
       *
       * Sign out only.
       *
       * DO NOT delete the Firebase account.
       */

      if (!isAuthorized) {
        await signOut(auth);

        localStorage.removeItem(
          "courseCode"
        );

        localStorage.removeItem(
          "orgCode"
        );

        localStorage.removeItem(
          "studentId"
        );

        localStorage.removeItem(
          "isAdmin"
        );

        navigate("/login", {
          replace: true,
        });

        return;
      }


      /*
       * Authorized.
       *
       * Navigate to the protected application.
       */

      console.log(
        "Application authorization: true"
      );

      navigate("/", {
        replace: true,
      });

    } catch (error) {
      console.error(
        "Sign-In Error:",
        error
      );


      /*
       * Clean up login-specific information.
       */

      localStorage.removeItem(
        "courseCode"
      );

      localStorage.removeItem(
        "orgCode"
      );

      localStorage.removeItem(
        "studentId"
      );

      localStorage.removeItem(
        "isAdmin"
      );


      /*
       * Display a useful error message.
       */

      let description =
        "Please enter valid credentials";

      if (
        error?.code ===
        "auth/user-not-found"
      ) {
        description =
          "No account was found with this email address.";
      } else if (
        error?.code ===
        "auth/wrong-password"
      ) {
        description =
          "The password is incorrect.";
      } else if (
        error?.code ===
        "auth/invalid-credential"
      ) {
        description =
          "The email or password is incorrect.";
      } else if (
        error?.code ===
        "auth/too-many-requests"
      ) {
        description =
          "Too many login attempts. Please try again later.";
      }


      toast({
        variant: "destructive",
        title: "Invalid Credentials!",
        description,
      });

    } finally {
      setAuthChecking(false);
    }
  };


  /*
   * ------------------------------------------------------------
   * GOOGLE SIGN-IN
   * ------------------------------------------------------------
   */

  const handleGoogleSignIn = async () => {
    if (authChecking) {
      return;
    }


    /*
     * Get values from the form.
     */

    const rawCode =
      form.getValues("code");

    const code =
      normalizeCode(rawCode);

    const orgCode =
      form.getValues("orgCode");


    /*
     * Basic organization validation.
     */

    if (!orgCode) {
      toast({
        variant: "destructive",
        title: "Organization Code Required!",
        description:
          "Please enter your organization code",
      });

      return;
    }


    /*
     * Validate course.
     */

    const validCourse =
      validateCourse();

    if (!validCourse) {
      return;
    }


    try {
      setAuthChecking(true);


      /*
       * IMPORTANT:
       *
       * Save organization and course BEFORE
       * calling signInWithPopup().
       *
       * Firebase can fire onAuthStateChanged
       * while the popup authentication is still
       * resolving.
       */

      localStorage.setItem(
        "courseCode",
        code
      );

      localStorage.setItem(
        "orgCode",
        orgCode
      );


      /*
       * Create Google provider.
       */

      const provider =
        new GoogleAuthProvider();


      /*
       * Optional:
       * Ask Google to show the account selector.
       *
       * This is particularly useful when the browser
       * has multiple Google accounts signed in.
       */

      provider.setCustomParameters({
        prompt: "select_account",
      });


      /*
       * Google authentication.
       */

      const result =
        await signInWithPopup(
          auth,
          provider
        );


      if (!result?.user) {
        throw new Error(
          "Google authentication did not return a user."
        );
      }


      const signedInUser =
        result.user;


      console.log(
        "Google login successful:",
        signedInUser.email
      );


      /*
       * --------------------------------------------------------
       * AUTHORIZE GOOGLE USER
       * --------------------------------------------------------
       */

      const isAuthorized =
        await authorizeUser(
          signedInUser.email,
          code,
          orgCode
        );


      /*
       * --------------------------------------------------------
       * UNAUTHORIZED GOOGLE ACCOUNT
       * --------------------------------------------------------
       *
       * IMPORTANT:
       *
       * Do NOT use deleteUser().
       *
       * The Firebase account may be a legitimate Google
       * account and deleting it is destructive.
       */

      if (!isAuthorized) {
        await signOut(auth);

        localStorage.removeItem(
          "courseCode"
        );

        localStorage.removeItem(
          "orgCode"
        );

        localStorage.removeItem(
          "studentId"
        );

        localStorage.removeItem(
          "isAdmin"
        );

        navigate("/login", {
          replace: true,
        });

        return;
      }


      /*
       * --------------------------------------------------------
       * AUTHORIZED
       * --------------------------------------------------------
       */

      console.log(
        "Application authorization: true"
      );

      navigate("/", {
        replace: true,
      });

    } catch (error) {
      console.error(
        "Google Sign-In Error:",
        error
      );


      /*
       * Clean up login-specific localStorage.
       */

      localStorage.removeItem(
        "courseCode"
      );

      localStorage.removeItem(
        "orgCode"
      );

      localStorage.removeItem(
        "studentId"
      );

      localStorage.removeItem(
        "isAdmin"
      );


      /*
       * Handle popup-specific errors.
       */

      let description =
        "Please try again later.";

      if (
        error?.code ===
        "auth/popup-closed-by-user"
      ) {
        description =
          "The Google sign-in window was closed.";
      } else if (
        error?.code ===
        "auth/popup-blocked"
      ) {
        description =
          "The browser blocked the Google sign-in popup. Please allow popups for this site.";
      } else if (
        error?.code ===
        "auth/cancelled-popup-request"
      ) {
        description =
          "Another Google sign-in request is already in progress.";
      } else if (
        error?.code ===
        "auth/account-exists-with-different-credential"
      ) {
        description =
          "An account already exists with a different sign-in method.";
      } else if (
        error?.code ===
        "auth/network-request-failed"
      ) {
        description =
          "A network error occurred. Please check your connection and try again.";
      }


      toast({
        variant: "destructive",
        title: "Login Error!",
        description,
      });

    } finally {
      setAuthChecking(false);
    }
  };


  /*
   * ------------------------------------------------------------
   * INITIAL AUTH CHECK
   * ------------------------------------------------------------
   *
   * Wait for AuthContext to finish checking the Firebase
   * session before showing the login form.
   *
   * We intentionally do NOT redirect based on `user` here.
   *
   * ProtectedLayout is responsible for routing authenticated
   * users after authentication is complete.
   */

  if (authLoading) {
    return (
      <Loading message={"Authenticating"} />
    );
  }


  /*
   * ------------------------------------------------------------
   * LOGIN OPERATION IN PROGRESS
   * ------------------------------------------------------------
   */

  if (authChecking) {
    return (
      <Loading message={"Authenticating"} />
    );
  }


  /*
   * ------------------------------------------------------------
   * LOGIN UI
   * ------------------------------------------------------------
   */

  return (
    <div className="flex w-full justify-center items-center h-screen">

      <Card className="w-[320px] md:w-[350px] shadow-lg">

        <CardHeader className="p-4 pb-0">

          <CardTitle className="text-xl">
            Edu Portal
          </CardTitle>

          <CardDescription className="text-base">
            Instructor - Naveen Pn
          </CardDescription>

        </CardHeader>


        <CardContent className="p-4">

          <Form {...form}>

            <form
              onSubmit={form.handleSubmit(
                handleSignIn
              )}
            >

              <div className="grid w-full items-center gap-4">


                {/* ------------------------------------------------
                    Organization Code
                ------------------------------------------------ */}

                <FormField
                  control={form.control}
                  name="orgCode"
                  render={({ field }) => (
                    <FormItem>

                      <FormControl>

                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter your organization code"
                          className="text-base py-3 h-10"
                        />

                      </FormControl>

                      <FormMessage />

                    </FormItem>
                  )}
                />


                {/* ------------------------------------------------
                    Course Code
                ------------------------------------------------ */}

                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>

                      <FormControl>

                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter your course code"
                          className="text-base py-3 h-10"
                        />

                      </FormControl>

                      <FormMessage />

                    </FormItem>
                  )}
                />


                {/* ------------------------------------------------
                    Corporate Login Checkbox
                ------------------------------------------------ */}

                <div className="flex items-center w-full gap-2">

                  <Checkbox
                    id="corporateLogin"
                    checked={corporateLogin}
                    onCheckedChange={(checked) =>
                      setCorporateLogin(
                        checked === true
                      )
                    }
                  />

                  <label
                    htmlFor="corporateLogin"
                    className="text-base cursor-pointer"
                  >
                    Corporate Login
                  </label>

                </div>


                {/* ------------------------------------------------
                    Google Login
                ------------------------------------------------ */}

                {!corporateLogin && (

                  <Button
                    type="button"
                    onClick={
                      handleGoogleSignIn
                    }
                    disabled={authChecking}
                    className="mt-1 text-base w-full py-5 m-auto font-medium"
                  >
                    Sign In with Google
                  </Button>

                )}


                {/* ------------------------------------------------
                    Corporate Email/Password Login
                ------------------------------------------------ */}

                {corporateLogin && (
                  <>

                    {/* Email */}

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>

                          <FormControl>

                            <Input
                              {...field}
                              type="email"
                              placeholder="Enter your email address"
                              className="text-base py-3 h-10"
                            />

                          </FormControl>

                          <FormMessage />

                        </FormItem>
                      )}
                    />


                    {/* Password */}

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>

                          <FormControl>

                            <Input
                              {...field}
                              type="password"
                              placeholder="Enter your password"
                              className="text-base py-3 h-10"
                            />

                          </FormControl>

                          <FormMessage />

                        </FormItem>
                      )}
                    />


                    {/* Login Button */}

                    <Button
                      type="submit"
                      disabled={authChecking}
                      className="text-base w-full py-5 m-auto font-medium"
                    >
                      Login with Email
                    </Button>

                  </>
                )}

              </div>

            </form>

          </Form>

        </CardContent>

      </Card>

    </div>
  );
};


export default Login;